interface EngagementOptions<Event extends string> {
  firstAction(channel: string): boolean;
  noAction(): void;
  noInteraction(): void;
  milestone(event: Event): void;
  milestones: ReadonlyArray<{ ms: number; event: Event }>;
  now?: () => number;
}

/** Own activity, inactivity, and the clock that counts only visible time. */
export function createSessionEngagement<Event extends string>(options: EngagementOptions<Event>) {
  type Activity = 'untouched' | 'interacted' | 'action_pending' | 'action_recorded';
  type Inactivity = { status: 'idle' | 'reported' } | { status: 'waiting'; timer: ReturnType<typeof setTimeout> };
  type Clock = { status: 'not_started' | 'disposed' }
    | { status: 'visible'; elapsed: number; since: number; timer: ReturnType<typeof setInterval> }
    | { status: 'hidden'; elapsed: number; timer: ReturnType<typeof setInterval> }
    | { status: 'complete' };
  const now = options.now ?? Date.now;
  let activity: Activity = 'untouched';
  let inactivity: Inactivity = { status: 'idle' };
  let clock: Clock = { status: 'not_started' };
  const recorded = new Set<string>();

  function checkpoint() {
    if (clock.status !== 'visible' && clock.status !== 'hidden') return;
    const elapsed = clock.elapsed + (clock.status === 'visible' ? now() - clock.since : 0);
    clock = clock.status === 'visible' ? { ...clock, elapsed, since: now() } : { ...clock, elapsed };
    for (const item of options.milestones) {
      if (elapsed < item.ms || recorded.has(item.event)) continue;
      recorded.add(item.event);
      options.milestone(item.event);
    }
    if (recorded.size === options.milestones.length) {
      clearInterval(clock.timer);
      clock = { status: 'complete' };
    }
  }

  function visible(value: boolean) {
    if (clock.status !== 'visible' && clock.status !== 'hidden') return;
    checkpoint();
    if (clock.status !== 'visible' && clock.status !== 'hidden') return;
    clock = value ? { status: 'visible', elapsed: clock.elapsed, since: now(), timer: clock.timer }
      : { status: 'hidden', elapsed: clock.elapsed, timer: clock.timer };
  }

  return {
    interact() { if (clock.status !== 'disposed' && activity === 'untouched') activity = 'interacted'; },
    act(channel: string) {
      if (clock.status === 'disposed' || activity === 'action_recorded') return;
      activity = 'action_pending';
      if (options.firstAction(channel)) activity = 'action_recorded';
    },
    startInactivity() {
      if (clock.status === 'disposed' || inactivity.status === 'reported') return;
      if (inactivity.status === 'waiting') clearTimeout(inactivity.timer);
      inactivity = { status: 'waiting', timer: setTimeout(() => {
        inactivity = { status: 'reported' };
        if (activity === 'action_pending' || activity === 'action_recorded') return;
        options.noAction();
        if (activity === 'untouched') options.noInteraction();
      }, 10_000) };
    },
    start(visibleNow: boolean) {
      if (clock.status !== 'not_started') return;
      const timer = setInterval(checkpoint, 1000);
      clock = visibleNow ? { status: 'visible', elapsed: 0, since: now(), timer }
        : { status: 'hidden', elapsed: 0, timer };
    },
    visible,
    hide() { visible(false); },
    dispose() {
      if (inactivity.status === 'waiting') clearTimeout(inactivity.timer);
      if (clock.status === 'visible' || clock.status === 'hidden') clearInterval(clock.timer);
      inactivity = { status: 'idle' };
      clock = { status: 'disposed' };
    },
  };
}
